import React, { useEffect, useState } from "react";
import ProductForm from "../Forms/ProductForm";
import { ProductFormData } from "../Forms/schemas/productFormSchema";
import { db } from "../Firebase/firebaseConfig";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import AdminProductTable from "./AdminTables/ProductTable"; // implement similarly to AdminNewsTable
import styles from "./createProduct.module.css";
import { Accordion, AccordionDetails, AccordionSummary, Typography, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";

const ManageProduct: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [defaultValues, setDefaultValues] = useState<ProductFormData>({
    name: "",
    subtitle: "",
    category: "",
    casNumber: "",
    base64Image: "",
    productDescription: "",
    brochure:"",
    productDetails: { sections: [] }, // match schema default shape
  });

  const handleSubmit = async (data: ProductFormData) => {
    setIsLoading(true);
    setStatusMessage(isEdit ? "Updating product..." : "Creating product...");
    setIsError(false);

    const payload: ProductFormData = {
      name: data.name.toLowerCase(),
      subtitle: data.subtitle.toLowerCase() || "",
      category: data.category.toLowerCase(),
      casNumber: data.casNumber || "",
      base64Image: data.base64Image || "",
      image: data.base64Image ? data.base64Image : "", // Ensure image is set
      productDescription: data.productDescription || "",
      productDetails: data.productDetails || { sections: [] },
      brochure: data.brochure || ""
    };
    

    try {
      // Prepare payload with server timestamp
      const productPayload = {
        ...payload,
        updatedAt: serverTimestamp(),
        ...(isEdit ? {} : { createdAt: serverTimestamp() }),
      };

      if (isEdit && editId) {
        const docRef = doc(db, "products", editId);
        await updateDoc(docRef, productPayload);
        setStatusMessage("Product updated successfully!");
        toast.success("Product updated successfully!");
      } else {
        await addDoc(collection(db, "products"), productPayload);
        setStatusMessage("Product created successfully!");
        toast.success("Product created successfully!");
      }

      // Reset form state
      resetDefaultValues();
      setIsEdit(false);
      setEditId(null);
    } catch (error: any) {
      setStatusMessage(`Error: ${error.message || "Submission failed."}`);
      toast.error(`Error: ${error.message || "Submission failed."}`);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (product: ProductFormData & { id: string }) => {
     
    setDefaultValues(product);
    setIsEdit(true);
    setEditId(product.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetDefaultValues = () => {
    setDefaultValues({
      name: "",
      subtitle: "",
      category: "",
      casNumber: "",
      base64Image: "",
      productDescription: "",
      productDetails: { sections: [] }, 
      brochure: ""
    });
  };

  useEffect(() => {
    if(isEdit) {
      setExpanded(true);
    }
  }, [isEdit]);

  return (
    <div className={styles.manageProductContainer}>
      <Accordion expanded={expanded}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={() => {
            setExpanded(!expanded);
            setIsEdit(false);
            resetDefaultValues();
          }}
        >
          <Typography variant="h6">{isEdit ? "Edit Product" : "Add New Product"}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div>
            {statusMessage && (
              <p
                className={`${styles.statusMessage} ${
                  isError ? styles.statusMessageError : styles.statusMessageSuccess
                }`}
              >
                {statusMessage}
              </p>
            )}

            {isLoading ? (
              <CircularProgress />
            ) : (
              <ProductForm onSubmit={handleSubmit} defaultValues={defaultValues} />
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      <AdminProductTable onEdit={handleEdit} />
    </div>
  );
};

export default ManageProduct;
